import React, { useState } from 'react';
import { NavBar } from '../components/NavBar';
import { Card } from '../components/SubscriptionAppCard';
import { Badge } from '../components/SubscriptionAppBadge';
import { Button } from '../components/SubscriptionAppButton';
import { BillingStatusBanner } from '../components/BillingStatusBanner';
import { useAppMode } from '../contexts/AppModeContext';
import { AlertTriangle, ChevronDown, ChevronUp, CreditCard, ExternalLink, Info, TrendingDown, TrendingUp } from 'lucide-react';
interface BillingLine {
  description: string;
  amount: string;
}
interface BillingRecord {
  id: string;
  date: string;
  planLine: BillingLine;
  /** Present only for periods where revenue ran above the included band */
  revenueLine?: BillingLine;
}
type AccountState = 'normal' | 'trial' | 'overdue' | 'limit';
type Recommendation = 'none' | 'upgrade' | 'downgrade';
type MemberState = 'normal' | 'approaching';
const shopifyBillingUrl = 'https://admin.shopify.com/settings/billing';
const shopifyPlansUrl = 'https://admin.shopify.com/charges/winepik/pricing_plans';
// Each Shopify billing period produces a base fee line, plus a revenue fee line
// in periods where revenue ran above the included band.
const billingRecords: BillingRecord[] = [{
  id: 'p-2025-12',
  date: '2025-12-15',
  planLine: {
    description: 'Growth Plan — Monthly',
    amount: '$299.00'
  },
  revenueLine: {
    description: 'Revenue fee · 15 Nov – 14 Dec 2025',
    amount: '$214.38'
  }
}, {
  id: 'p-2025-11',
  date: '2025-11-15',
  planLine: {
    description: 'Growth Plan — Monthly',
    amount: '$299.00'
  }
}, {
  id: 'p-2025-10',
  date: '2025-10-15',
  planLine: {
    description: 'Growth Plan — Monthly',
    amount: '$299.00'
  },
  revenueLine: {
    description: 'Revenue fee · 15 Sep – 14 Oct 2025',
    amount: '$342.13'
  }
}, {
  id: 'p-2025-09',
  date: '2025-09-15',
  planLine: {
    description: 'Growth Plan — Monthly',
    amount: '$299.00'
  }
}, {
  id: 'p-2025-08',
  date: '2025-08-15',
  planLine: {
    description: 'Growth Plan — Monthly',
    amount: '$299.00'
  }
}, {
  id: 'p-2025-07',
  date: '2025-07-15',
  planLine: {
    description: 'Growth Plan — Monthly',
    amount: '$299.00'
  }
}];
// Growth plan: $299 base includes the first $5,000/mo, then 1.75% above it.
const billingPeriod = {
  start: '15 Dec 2025',
  end: '14 Jan 2026',
  chargeDate: '15 Jan 2026',
  baseFee: 299,
  includedBand: 5000,
  rate: '1.75%',
  revenue: 12400,
  feeAccrued: 129.5,
  customRevenue: 18600
};
// Loyalty-only merchants have no subscription revenue — their plan is set by
// how many active loyalty members they carry.
const loyaltyPeriod = {
  includedMembers: 7500,
  activeMembers: 5240,
  approachingMembers: 7180
};
const trialDetails = {
  daysRemaining: 4,
  totalDays: 7,
  firstChargeDate: '15 Jan 2026',
  firstChargeAmount: 299
};
const formatMoney = (amount: number): string => '$' + amount.toLocaleString('en-US', {
  minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  maximumFractionDigits: 2
});
export function SettingsAccountBillingPage() {
  const {
    isLoyaltyOnly,
    setMode
  } = useAppMode();
  const [billingExpanded, setBillingExpanded] = useState(false);
  const [previewState, setPreviewState] = useState<'public' | 'custom'>('public');
  const [accountState, setAccountState] = useState<AccountState>('normal');
  const [recommendation, setRecommendation] = useState<Recommendation>('none');
  const [memberState, setMemberState] = useState<MemberState>('normal');
  const [feeExplainerOpen, setFeeExplainerOpen] = useState(false);
  const nextInvoiceTotal = billingPeriod.baseFee + billingPeriod.feeAccrued;
  const activeMembers = memberState === 'approaching' ? loyaltyPeriod.approachingMembers : loyaltyPeriod.activeMembers;
  const memberHeadroom = Math.max(loyaltyPeriod.includedMembers - activeMembers, 0);
  const memberUsedPct = Math.min(Math.round(activeMembers / loyaltyPeriod.includedMembers * 100), 100);
  const formatCount = (value: number): string => value.toLocaleString('en-US');
  const displayedRecords = billingExpanded ? billingRecords : billingRecords.slice(0, 3);
  const currentPlanName = previewState === 'public' ? 'Growth Plan' : 'Custom plan';
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const openShopifyPlans = () => window.open(shopifyPlansUrl, '_blank', 'noopener,noreferrer');
  const scrollToPlans = () => {
    document.getElementById('plans')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const controlButtonClass = (active: boolean) => `h-[32px] px-3 rounded-[4px] text-[13px] font-medium transition-colors ${active ? 'bg-[#E0F2FE] text-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`;
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-12872" data-mp="355k9rp0smt80">
      <div className="mb-6" data-id="element-12873" data-mp="356vg9wlmif2t">
        <NavBar tabs={[]} title="Account & Billing" subtitle="Manage your WinePik subscription plan and billing history" data-id="element-12874" data-mp="357nbv7f80ct3" />
      </div>

      <main className="space-y-6" data-id="element-12875" data-mp="3f38i5hrbnz8n">
        {/* Prototype-only preview controls */}
        <div className="space-y-2" data-id="element-12876" data-mp="3j4ttuzs4e7h9">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center" data-id="element-12877" data-mp="3j5v1svduadur">
            <span className="text-[13px] font-medium text-[#64748B]" data-id="element-12878" data-mp="3o4y9yop4906y">App mode:</span>
            <div className="inline-flex w-fit rounded-md border border-[#CBD5E1] bg-white p-1" role="group" aria-label="Prototype app mode preview" data-id="element-12879" data-mp="3t2zp4yjf8fd5">
              <button type="button" onClick={() => setMode('club-loyalty')} aria-pressed={!isLoyaltyOnly} className={controlButtonClass(!isLoyaltyOnly)} data-id="element-12880" data-mp="1pdjqlbawuhp">
                Club + loyalty
              </button>
              <button type="button" onClick={() => setMode('loyalty-only')} aria-pressed={isLoyaltyOnly} className={controlButtonClass(isLoyaltyOnly)} data-id="element-12881" data-mp="ar36vey5h79o">
                Loyalty only
              </button>
            </div>
            <span className="text-[12px] text-[#94A3B8]" data-id="element-12882" data-mp="27pwecyvbq323">Prototype control</span>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center" data-id="element-12883" data-mp="27o383komhkum">
            <span className="text-[13px] font-medium text-[#64748B]" data-id="element-12884" data-mp="2lim1x7qi3g1b">Preview state:</span>
            <div className="inline-flex w-fit rounded-md border border-[#CBD5E1] bg-white p-1" role="group" aria-label="Prototype billing state preview" data-id="element-12885" data-mp="2gp18lszura8g">
              <button type="button" onClick={() => setPreviewState('public')} aria-pressed={previewState === 'public'} className={controlButtonClass(previewState === 'public')} data-id="element-12886" data-mp="2qgxc5jkpjsk2">
                Public plan
              </button>
              <button type="button" onClick={() => setPreviewState('custom')} aria-pressed={previewState === 'custom'} className={controlButtonClass(previewState === 'custom')} data-id="element-12887" data-mp="2zidkllrypkln">
                Custom plan
              </button>
            </div>
            <span className="text-[12px] text-[#94A3B8]" data-id="element-12888" data-mp="3gqej7fe59phx">Prototype control</span>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center" data-id="element-12889" data-mp="3gsr0rrfzpxug">
            <span className="text-[13px] font-medium text-[#64748B]" data-id="element-12890" data-mp="3lopqpjgyvnwz">Account state:</span>
            <div className="inline-flex w-fit rounded-md border border-[#CBD5E1] bg-white p-1" role="group" aria-label="Prototype account state preview" data-id="element-12891" data-mp="37xkxp083ttvm">
              {[{
              id: 'normal' as const,
              label: 'Normal'
            }, {
              id: 'trial' as const,
              label: 'Trial'
            }, {
              id: 'overdue' as const,
              label: 'Payment overdue'
            }, {
              id: 'limit' as const,
              label: 'Plan limit reached'
            }].map(option => <button key={option.id} type="button" onClick={() => setAccountState(option.id)} aria-pressed={accountState === option.id} className={controlButtonClass(accountState === option.id)} data-id="element-12892" data-mp="2t2n5c7cyh4ej">
                  {option.label}
                </button>)}
            </div>

            {!isLoyaltyOnly && <>
                <span className="text-[13px] font-medium text-[#64748B] sm:ml-4" data-id="element-12893" data-mp="366oiwp9hipyg">Recommendation:</span>
                <div className="inline-flex w-fit rounded-md border border-[#CBD5E1] bg-white p-1" role="group" aria-label="Prototype recommendation preview" data-id="element-12894" data-mp="31fexk0uyy8sd">
                  {[{
                id: 'none' as const,
                label: 'None'
              }, {
                id: 'upgrade' as const,
                label: 'Upgrade'
              }, {
                id: 'downgrade' as const,
                label: 'Downgrade'
              }].map(option => <button key={option.id} type="button" onClick={() => setRecommendation(option.id)} aria-pressed={recommendation === option.id} className={controlButtonClass(recommendation === option.id)} data-id="element-12895" data-mp="2ihc9ppe5qhw5">
                      {option.label}
                    </button>)}
                </div>
              </>}
          </div>

          {isLoyaltyOnly && <div className="flex flex-col gap-2 sm:flex-row sm:items-center" data-id="element-12896" data-mp="k7mx0w14p895">
            <span className="text-[13px] font-medium text-[#64748B]" data-id="element-12897" data-mp="k5ka4kv9vv8n">Member state:</span>
            <div className="inline-flex w-fit rounded-md border border-[#CBD5E1] bg-white p-1" role="group" aria-label="Prototype loyalty member state preview" data-id="element-12898" data-mp="o6vsc5bthe6t">
              {[{
              id: 'normal' as const,
              label: 'Normal'
            }, {
              id: 'approaching' as const,
              label: 'Approaching ceiling'
            }].map(option => <button key={option.id} type="button" onClick={() => setMemberState(option.id)} aria-pressed={memberState === option.id} className={controlButtonClass(memberState === option.id)} data-id="element-12899" data-mp="5b5qqj7sqjtg">
                  {option.label}
                </button>)}
            </div>
          </div>}
        </div>

        {/* Shared billing status banner — one banner, picked by severity */}
        <BillingStatusBanner paymentOverdue={accountState === 'overdue'} planLimitReached={accountState === 'limit'} inTrial={accountState === 'trial'} trialDaysRemaining={trialDetails.daysRemaining} trialTotalDays={trialDetails.totalDays} trialFirstChargeDate={trialDetails.firstChargeDate} trialFirstChargeAmount={trialDetails.firstChargeAmount} upgradePlanName="Growth" data-id="element-12900" data-mp="1qmxkwuure7o2" />

        <Card padding="none" data-id="element-12901" data-mp="11g31tr91txfv">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between gap-4" data-id="element-12902" data-mp="11gcmczbbrce6">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-12903" data-mp="4qlmekk9kx0z">Current Plan</h2>
            {previewState === 'public' && <Button variant="secondary" onClick={scrollToPlans} data-id="element-12904" data-mp="9ppzhmab7ewl">Change Plan</Button>}
          </div>

          <div className="p-6" data-id="element-12905" data-mp="dpqp832uzciy">
            <div className="mb-6" data-id="element-12906" data-mp="dsuyvfz3f0h0">
              {previewState === 'public' ? <>
                  <div className="flex items-center gap-3 mb-2" data-id="element-12907" data-mp="iqwefqxb8hnq">
                    <h3 className="text-[24px] font-semibold text-[#0EA5E9]" data-id="element-12908" data-mp="1p4nftheoy9sf">Growth Plan</h3>
                    <Badge variant="success" data-id="element-12909" data-mp="1u3yven3bn98f">Active</Badge>
                  </div>
                  <p className="text-[18px] text-[#475569] mb-1" data-id="element-12910" data-mp="2498brzp62jlx">$299/month</p>
                  <p className="text-[14px] text-[#64748B] mb-3" data-id="element-12911" data-mp="1z9lwzltowvb0">Billed monthly · Renews Jan 15, 2026</p>
                  <p className="text-[14px] text-[#64748B]" data-id="element-12912" data-mp="1uivq245m8ke7">Up to 7,500 active loyalty members · Full loyalty program included</p>
                </> : <>
                  <div className="flex items-center gap-3 mb-2" data-id="element-12913" data-mp="1qgse7ef1bdse">
                    <h3 className="text-[24px] font-semibold text-[#0EA5E9]" data-id="element-12914" data-mp="1lih6b98avdct">Custom plan</h3>
                    <Badge variant="default" data-id="element-12915" data-mp="1gikzz3c4r9u3">Agreed with WinePik</Badge>
                  </div>
                  <p className="text-[18px] text-[#475569] mb-1" data-id="element-12916" data-mp="1bjhu5gobszl2">$399/month</p>
                  <p className="text-[14px] text-[#64748B]" data-id="element-12917" data-mp="17iftcvergfgz">Custom revenue and member allowances agreed with WinePik — see your agreement for details.</p>
                </>}
            </div>

            <div className="p-4 bg-[#E0F2FE] border border-[#7DD3FC] rounded-md flex items-start gap-3" data-id="element-12918" data-mp="35vpwhf2z8iqv">
              <Info className="w-5 h-5 text-[#0369A1] flex-shrink-0 mt-0.5" data-id="element-12919" data-mp="31umdijpmu6lf" />
              <div className="flex-1" data-id="element-12920" data-mp="z8h4q6uaidk4">
                <p className="text-[14px] text-[#0369A1] mb-2" data-id="element-12921" data-mp="1408nvwb49h08">
                  Your WinePik subscription is managed through the Shopify App Store. To change your plan or update payment details, visit your Shopify admin.
                </p>
                <a href={shopifyBillingUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-12922" data-mp="ub8jfxduhten">
                  Manage in Shopify
                  <ExternalLink className="w-3.5 h-3.5" data-id="element-12923" data-mp="18zlfsvguhe9o" />
                </a>
              </div>
            </div>
          </div>
        </Card>

        <Card padding="none" data-id="element-12924" data-mp="1dxd8vst876fg">
          <div className="p-6 border-b border-[#E2E8F0]" data-id="element-12925" data-mp="1e0spj02jsniz">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-12926" data-mp="3tosptngtldiu">
              This billing period
            </h2>
            <p className="text-[14px] text-[#64748B] mt-1" data-id="element-12927" data-mp="2l42y9t7hqip">
              {billingPeriod.start} – {billingPeriod.end}
            </p>
            <p className="text-[13px] text-[#94A3B8] mt-1" data-id="element-12928" data-mp="3m6berctapqnr">
              This covers your Shopify 30 day billing cycle, not a calendar
              month. All amounts are USD.
            </p>
          </div>

          {isLoyaltyOnly ? previewState === 'public' ? <div className="p-6" data-id="element-12929" data-mp="3v8ave40eesq6">
                {memberState === 'approaching' && <div className="mb-6 p-4 rounded-md border border-[#FCD34D] bg-[#FFFBEB] flex flex-col gap-4 md:flex-row md:items-center md:justify-between" data-id="element-12930" data-mp="37gvmfse49d5y">
                    <div className="flex items-start gap-3" data-id="element-12931" data-mp="32ghfkdo9499i">
                      <AlertTriangle className="w-5 h-5 text-[#B45309] flex-shrink-0 mt-0.5" data-id="element-12932" data-mp="3h802ywjfx62w" />
                      <p className="text-[14px] text-[#92400E]" data-id="element-12933" data-mp="3cfq6exntl7rn">
                        Heads up — you're close to the {formatCount(loyaltyPeriod.includedMembers)} active
                        members your plan includes. Passing it means moving up to
                        the next plan, which costs more each month.
                      </p>
                    </div>
                    <div className="flex-shrink-0" data-id="element-12934" data-mp="1rz0u722ji9b1">
                      <Button variant="secondary" onClick={openShopifyPlans} iconRight={<ExternalLink className="w-4 h-4" data-id="element-12936" data-mp="1w02sngomuhby" />} data-id="element-12935" data-mp="1rzsg1l6c4so4">
                        View plans in Shopify
                      </Button>
                    </div>
                  </div>}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-12937" data-mp="25rqf136c186k">
                  <div data-id="element-12938" data-mp="2aq1nb9gzbamo">
                    <p className="text-[13px] text-[#64748B] mb-1" data-id="element-12939" data-mp="2ap8rh3jk65jb">
                      Active loyalty members
                    </p>
                    <p className="text-[24px] font-semibold text-[#0F172A]" data-id="element-12940" data-mp="2fpms40ityd7b">
                      {formatCount(activeMembers)}
                    </p>
                  </div>
                  <div data-id="element-12941" data-mp="lc58uppgk2i3">
                    <p className="text-[13px] text-[#64748B] mb-1" data-id="element-12942" data-mp="lcwtx6lfihu4">
                      Included in your plan
                    </p>
                    <p className="text-[24px] font-semibold text-[#0F172A]" data-id="element-12943" data-mp="ht686gw4p8bl">
                      {formatCount(loyaltyPeriod.includedMembers)}
                    </p>
                  </div>
                  <div data-id="element-12944" data-mp="83ctv84lqf3n">
                    <p className="text-[13px] text-[#64748B] mb-1" data-id="element-12945" data-mp="82v1wnoz2n1j">Headroom</p>
                    <p className="text-[24px] font-semibold text-[#0F172A]" data-id="element-12946" data-mp="41ro06qhj12y">
                      {formatCount(memberHeadroom)}
                    </p>
                  </div>
                </div>

                <div className="mt-6" data-id="element-12947" data-mp="vsxpaqhxz4tt">
                  <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2" data-id="element-12948" data-mp="vu0gz0n39bas">
                    <p className="text-[13px] font-medium text-[#475569]" data-id="element-12949" data-mp="rqatkei5gzrc">
                      Active members against your plan
                    </p>
                    <p className="text-[13px] text-[#64748B]" data-id="element-12950" data-mp="mulojvnd2qnn">
                      {formatCount(activeMembers)} of{' '}
                      {formatCount(loyaltyPeriod.includedMembers)} · {memberUsedPct}% used
                    </p>
                  </div>
                  <div className="h-2 w-full rounded-full bg-[#E2E8F0] overflow-hidden" role="progressbar" aria-valuenow={memberUsedPct} aria-valuemin={0} aria-valuemax={100} aria-label="Active loyalty members against plan" data-id="element-12951" data-mp="390yo4wkpw4tq">
                    <div className={`h-full rounded-full ${memberState === 'approaching' ? 'bg-[#F59E0B]' : 'bg-[#0EA5E9]'}`} style={{
                width: `${memberUsedPct}%`
              }} data-id="element-12952" data-mp="3011t8d7qi9q8" />
                  </div>
                  <p className="text-[14px] text-[#64748B] mt-3" data-id="element-12953" data-mp="2uzw1oipt8sia">
                    Your plan is set by how many active loyalty members you
                    have. An active member is one who has earned or redeemed
                    points in the last twelve months.
                  </p>
                </div>

                <div className="mt-6 p-5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md" data-id="element-12954" data-mp="279rygcgjaurt">
                  <p className="text-[13px] font-medium text-[#64748B] mb-1" data-id="element-12955" data-mp="2c94unntape72">
                    Expected on your next invoice
                  </p>
                  <p className="text-[32px] font-semibold text-[#0F172A] leading-tight" data-id="element-12956" data-mp="1so4lbb8r0qig">
                    {formatMoney(billingPeriod.baseFee)}
                  </p>
                  <p className="text-[14px] text-[#475569] mt-2" data-id="element-12957" data-mp="21oj8yh96qhp4">
                    Your plan base fee, charged {billingPeriod.chargeDate}. There
                    is no revenue fee on a loyalty only plan.
                  </p>
                </div>

                <div className="mt-6 pt-6 border-t border-[#E2E8F0]" data-id="element-12958" data-mp="2bk4724b4uxtl">
                  <button type="button" onClick={() => setFeeExplainerOpen(!feeExplainerOpen)} aria-expanded={feeExplainerOpen} aria-controls="plan-basis-explainer" className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-12959" data-mp="26mukqxj1j29z">
                    How your plan is set
                    {feeExplainerOpen ? <ChevronUp className="w-4 h-4" data-id="element-12960" data-mp="2pe5ahbd1590a" /> : <ChevronDown className="w-4 h-4" data-id="element-12961" data-mp="2pf6krdm81rgy" />}
                  </button>
                  {feeExplainerOpen && <p id="plan-basis-explainer" className="text-[14px] text-[#64748B] mt-3" data-id="element-12962" data-mp="k58ten6mkuq">
                      Loyalty only plans are priced on your active member count
                      rather than order revenue. An active member is one who
                      earned or redeemed points in the last twelve months.
                      Migrated members carrying a points balance count from the
                      date they were imported.
                    </p>}
                </div>
              </div> : <div className="p-6" data-id="element-12963" data-mp="3i1y6qmwfx2f9">
                <div data-id="element-12964" data-mp="3hz2bj6bz8rui">
                  <p className="text-[13px] text-[#64748B] mb-1" data-id="element-12965" data-mp="3mzzpt7tv91qq">
                    Active loyalty members
                  </p>
                  <p className="text-[24px] font-semibold text-[#0F172A]" data-id="element-12966" data-mp="3rrzu1cgyygt4">
                    {formatCount(activeMembers)}
                  </p>
                </div>
                <p className="text-[14px] text-[#64748B] mt-4" data-id="element-12967" data-mp="2zaf44h6bvqdr">
                  Your plan is a fixed monthly fee agreed with WinePik.
                </p>
              </div> : previewState === 'public' ? <div className="p-6" data-id="element-12968" data-mp="2mskvaqqe9c1b">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-12969" data-mp="2mtmi1sgrvmyv">
                <div data-id="element-12970" data-mp="2hsp0ni3l6wyf">
                  <p className="text-[13px] text-[#64748B] mb-1" data-id="element-12971" data-mp="2hs6yk4u8toil">
                    Subscription revenue counted this period
                  </p>
                  <p className="text-[24px] font-semibold text-[#0F172A]" data-id="element-12972" data-mp="2dtzcqvkx9x0j">
                    {formatMoney(billingPeriod.revenue)}
                  </p>
                </div>
                <div data-id="element-12973" data-mp="240ikhw0fopyz">
                  <p className="text-[13px] text-[#64748B] mb-1" data-id="element-12974" data-mp="2400fa9vsn1ex">
                    Included in your base
                  </p>
                  <p className="text-[24px] font-semibold text-[#0F172A]" data-id="element-12975" data-mp="1u45r6z8swe4r">
                    {formatMoney(billingPeriod.includedBand)}
                  </p>
                </div>
                <div data-id="element-12976" data-mp="1l2zc8ulsvbdm">
                  <p className="text-[13px] text-[#64748B] mb-1" data-id="element-12977" data-mp="1l2pgsrhzkc0j">
                    Revenue fee so far
                  </p>
                  <p className="text-[24px] font-semibold text-[#0F172A]" data-id="element-12978" data-mp="9yzhq0a41jqr">
                    {formatMoney(billingPeriod.feeAccrued)}
                  </p>
                  <p className="text-[13px] text-[#64748B] mt-1" data-id="element-12979" data-mp="ykxl48vuaou">
                    {billingPeriod.rate} on revenue above your included band
                  </p>
                </div>
              </div>

              {/* The expected invoice is now the headline figure on this card */}
              <div className="mt-6 p-5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md" data-id="element-12980" data-mp="3ifcelreg1bjq">
                <p className="text-[13px] font-medium text-[#64748B] mb-1" data-id="element-12981" data-mp="3idhxypa7g5lg">
                  Expected on your next invoice
                </p>
                <p className="text-[32px] font-semibold text-[#0F172A] leading-tight" data-id="element-12982" data-mp="39fpd75lps5qo">
                  {formatMoney(nextInvoiceTotal)}
                </p>
                <p className="text-[14px] text-[#475569] mt-2" data-id="element-12983" data-mp="2zggnioxvtda4">
                  {formatMoney(billingPeriod.baseFee)} base plus{' '}
                  {formatMoney(billingPeriod.feeAccrued)} revenue fee, charged{' '}
                  {billingPeriod.chargeDate}.
                </p>
              </div>

              <div className="mt-6 pt-6 border-t border-[#E2E8F0]" data-id="element-12984" data-mp="2zaprhtvuzqp7">
                <button type="button" onClick={() => setFeeExplainerOpen(!feeExplainerOpen)} aria-expanded={feeExplainerOpen} aria-controls="revenue-fee-explainer" className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-12985" data-mp="2zbhb06c6lxz4">
                  How the revenue fee is calculated
                  {feeExplainerOpen ? <ChevronUp className="w-4 h-4" data-id="element-12986" data-mp="2gjyemicnxi62" /> : <ChevronDown className="w-4 h-4" data-id="element-12987" data-mp="2gix18786cjl6" />}
                </button>
                {feeExplainerOpen && <p id="revenue-fee-explainer" className="text-[14px] text-[#64748B] mt-3" data-id="element-12988" data-mp="2lc9ddhvo2xx1">
                    The fee applies to subscription order revenue processed
                    through WinePik, excluding freight and tax, and net of
                    refunds. It's charged as a single line at the end of each
                    billing period.
                  </p>}
              </div>
            </div> : <div className="p-6" data-id="element-12989" data-mp="1fss6ta40m2uf">
              <div data-id="element-12990" data-mp="1ujhjyi11ds0x">
                <p className="text-[13px] text-[#64748B] mb-1" data-id="element-12991" data-mp="1uk0uhdocxe4x">
                  Subscription revenue counted this period
                </p>
                <p className="text-[24px] font-semibold text-[#0F172A]" data-id="element-12992" data-mp="1pmr51y0yx2h3">
                  {formatMoney(billingPeriod.customRevenue)}
                </p>
              </div>
              <p className="text-[14px] text-[#64748B] mt-4" data-id="element-12993" data-mp="wxadt3p33rmg">
                Your plan is a fixed monthly fee agreed with WinePik.
              </p>
            </div>}
        </Card>

        <section id="plans" aria-labelledby="change-plan-heading" className="space-y-6" data-id="element-12994" data-mp="16twwpmxuo8lu">
          {!isLoyaltyOnly && recommendation !== 'none' && <div className="p-4 rounded-md border border-[#7DD3FC] bg-[#E0F2FE] flex flex-col gap-4 md:flex-row md:items-center md:justify-between" data-id="element-12995" data-mp="2n6lkoat3jk6w">
              <div className="flex items-start gap-3" data-id="element-12996" data-mp="2psbbirxvgjy7">
                {recommendation === 'upgrade' ? <TrendingUp className="w-5 h-5 text-[#0369A1] flex-shrink-0 mt-0.5" data-id="element-12997" data-mp="2urnwt893hiyo" /> : <TrendingDown className="w-5 h-5 text-[#0369A1] flex-shrink-0 mt-0.5" data-id="element-12998" data-mp="2upl065dfsrkz" />}
                <p className="text-[14px] text-[#0369A1]" data-id="element-12999" data-mp="2gu96m1b0p75n">
                  {recommendation === 'upgrade' ? 'On your revenue over the last three months, you would pay $84/month less on Pro.' : 'Over the last twelve months, including your busiest months, you would have paid $612 less on Growth.'}
                </p>
              </div>
              <div className="flex-shrink-0" data-id="element-13000" data-mp="2zrafvyblttfe">
                <Button variant="primary" onClick={openShopifyPlans} iconRight={<ExternalLink className="w-4 h-4" data-id="element-13002" data-mp="33ru9wt0vsgxb" />} data-id="element-13001" data-mp="2zqiqx6ciity3">
                  View plans in Shopify
                </Button>
              </div>
            </div>}

          <Card padding="none" data-id="element-13003" data-mp="3rg3tdoamgsla">
            <div className="p-6 border-b border-[#E2E8F0]" data-id="element-13004" data-mp="42dundvg2hd">
              <h2 id="change-plan-heading" className="text-[20px] font-semibold text-[#0F172A]" data-id="element-13005" data-mp="5n9kgbx9rgt">
                Change your plan
              </h2>
            </div>
            <div className="p-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between" data-id="element-13006" data-mp="23kqsxx1puyso">
              <div data-id="element-13007" data-mp="1yn75c6j4p9o2">
                <p className="text-[14px] text-[#64748B]" data-id="element-13008" data-mp="1ympa9d87d1hq">
                  Plans are managed in your Shopify admin. Choosing a plan there
                  approves the recurring charge and applies it to WinePik.
                </p>
                <p className="text-[14px] text-[#475569] mt-2" data-id="element-13009" data-mp="2mc9vy3vqqcup">
                  Current plan:{' '}
                  <span className="font-semibold text-[#0F172A]" data-id="element-13010" data-mp="2hl1nba28ivil">
                    {currentPlanName}
                  </span>
                </p>
              </div>
              <div className="flex-shrink-0" data-id="element-13011" data-mp="2dhlruh1wj17h">
                <Button variant="primary" onClick={openShopifyPlans} iconRight={<ExternalLink className="w-4 h-4" data-id="element-13013" data-mp="3a3fc6k5lf75j" />} data-id="element-13012" data-mp="3a1ufoozpxe51">
                  View plans in Shopify
                </Button>
              </div>
            </div>
          </Card>
        </section>

        <Card padding="none" data-id="element-13014" data-mp="rub13ke9zp8q">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between" data-id="element-13015" data-mp="wr1gs6m64itm">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-13016" data-mp="10t65qs1o8r9x">Billing History</h2>
            <button onClick={() => setBillingExpanded(!billingExpanded)} className="flex items-center gap-2 text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" aria-expanded={billingExpanded} data-id="element-13017" data-mp="15rzj8kgykia3">
              <span data-id="element-13018" data-mp="1fpndzaekdps9">{billingExpanded ? 'Show Less' : 'Show More'}</span>
              {billingExpanded ? <ChevronUp className="w-4 h-4" data-id="element-13019" data-mp="1kh5j8anmecj1" /> : <ChevronDown className="w-4 h-4" data-id="element-13020" data-mp="1ki6ticwtauzp" />}
            </button>
          </div>

          <div className="overflow-hidden transition-all duration-250 ease-in-out" data-id="element-13021" data-mp="1oiqslm0y48oh">
            <div className="overflow-x-auto" data-id="element-13022" data-mp="1ti3m2pmboe01">
              <table className="w-full" data-id="element-13023" data-mp="1tjer0sri7rsq">
                <thead className="bg-[#F1F5F9]" data-id="element-13024" data-mp="1ygeuqx0vsigo">
                  <tr data-id="element-13025" data-mp="1yh6enal48rr4">
                    <th className="px-6 py-3 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-13026" data-mp="1yebw5olnxh0c">Date</th>
                    <th className="px-6 py-3 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-13027" data-mp="3pl8by0tsam7y">Description</th>
                    <th className="px-6 py-3 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-13028" data-mp="85t7wy3gzate">Amount</th>
                    <th className="px-6 py-3 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-13029" data-mp="3boy872tkoom">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-13030" data-mp="d31tz9rqnyzx">
                  {displayedRecords.map((record, index) => <React.Fragment key={record.id} data-id="element-13031" data-mp="r10422z1199m" data-mpi={index}>
                      <tr className="hover:bg-[#F8FAFC] transition-colors h-[56px]" style={{
                    opacity: !billingExpanded && index === 2 ? 0.6 : 1
                  }} data-id="element-13032" data-mp="m3q7829b5vbr">
                        <td className="px-6 py-4 text-[14px] text-[#475569]" data-id="element-13033" data-mp="2nvw6est1t092">{formatDate(record.date)}</td>
                        <td className="px-6 py-4 text-[14px] text-[#0F172A]" data-id="element-13034" data-mp="13d25pkltentv">{record.planLine.description}</td>
                        <td className="px-6 py-4 text-[14px] font-semibold text-[#0F172A]" data-id="element-13035" data-mp="18bdhw1zl1af9">{record.planLine.amount}</td>
                        <td className="px-6 py-4" data-id="element-13036" data-mp="tmzf1r261lyp"><Badge variant="success" data-id="element-13037" data-mp="tlocfukgik96">Paid</Badge></td>
                      </tr>
                      {record.revenueLine && <tr className="hover:bg-[#F8FAFC] transition-colors h-[56px]" style={{
                    opacity: !billingExpanded && index === 2 ? 0.6 : 1
                  }} data-id="element-13038" data-mp="kmu97lcmevmn">
                          <td className="px-6 py-4" data-id="element-13039" data-mp="pjkpo9sc7t8l" />
                          <td className="px-6 py-4 text-[14px] text-[#475569] pl-10" data-id="element-13040" data-mp="apy8i1syrdsn">{record.revenueLine.description}</td>
                          <td className="px-6 py-4 text-[14px] font-semibold text-[#0F172A]" data-id="element-13041" data-mp="fnzqejenl72j">{record.revenueLine.amount}</td>
                          <td className="px-6 py-4" data-id="element-13042" data-mp="2a1sb792cgipu"><Badge variant="success" data-id="element-13043" data-mp="2a2afmsz5u38u">Paid</Badge></td>
                        </tr>}
                    </React.Fragment>)}
                </tbody>
              </table>
            </div>

            <div className="px-6 py-4 border-t border-[#E2E8F0] space-y-1" data-id="element-13044" data-mp="ssso9fzbx3xf">
              <p className="text-[14px] text-[#64748B]" data-id="element-13045" data-mp="svooxc2ka6pk">
                Receipts for these charges live in your Shopify billing
                settings.{' '}
                <a href={shopifyBillingUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-13046" data-mp="j2zpjfkacjal">
                  View in Shopify
                  <ExternalLink className="w-3.5 h-3.5" data-id="element-13047" data-mp="a579282tv5lm" />
                </a>
              </p>
              <p className="text-[14px] text-[#64748B]" data-id="element-13048" data-mp="55udmyxw4q7f">
                History covers the last twelve months. Older periods aren't
                available.
              </p>
            </div>
          </div>
        </Card>

        <Card padding="none" data-id="element-13049" data-mp="1uixt53wp2d4f">
          <div className="p-6 border-b border-[#E2E8F0]" data-id="element-13050" data-mp="1ujpp4fu7ecv8">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-13051" data-mp="22270krln41qu">Payment Method</h2>
          </div>

          <div className="p-6" data-id="element-13052" data-mp="2710gsrsba6up">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-id="element-13053" data-mp="2708ktfusy73w">
              <div className="flex items-start gap-3" data-id="element-13054" data-mp="2bt1nzzamh7dv">
                <CreditCard className="w-6 h-6 text-[#475569] flex-shrink-0" data-id="element-13055" data-mp="2gr3j5ilbox5p" />
                <div data-id="element-13056" data-mp="2gt66tvz06e79">
                  <p className="text-[16px] font-medium text-[#0F172A]" data-id="element-13057" data-mp="1j3cndn1tr9cs">
                    Held by Shopify
                  </p>
                  <p className="text-[14px] text-[#64748B] mt-1" data-id="element-13058" data-mp="1o1mk42376i0i">
                    The payment method for WinePik is the one on your Shopify
                    account. Shopify doesn't share card details with apps, so
                    they're only visible and editable in your Shopify admin.
                  </p>
                </div>
              </div>
              <a href="https://admin.shopify.com/settings/payments" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors flex-shrink-0" data-id="element-13059" data-mp="34j0lmj2vucu5">
                Update in Shopify
                <ExternalLink className="w-3.5 h-3.5" data-id="element-13060" data-mp="6zdibi11swj9" />
              </a>
            </div>
          </div>
        </Card>

        <Card padding="none" className="border-[#FCA5A5]" data-id="element-13061" data-mp="3tcu677wm4bac">
          <div className="p-6 border-b border-[#FCA5A5] bg-[#FEF2F2]" data-id="element-13062" data-mp="3tbaordz7zu6z">
            <h2 className="text-[20px] font-semibold text-[#B91C1C]" data-id="element-13063" data-mp="3fct45p4m2xta">Danger Zone</h2>
          </div>

          <div className="p-6 bg-[#FEF2F2]" data-id="element-13064" data-mp="3kcfhdykfwe23">
            <div className="mb-4" data-id="element-13065" data-mp="3ka2t73o397gl">
              <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-13066" data-mp="36cwbzdj0jgth">Cancel your subscription</h3>
              <p className="text-[14px] text-[#64748B] mb-4" data-id="element-13067" data-mp="3adg8cevrigek">To cancel your WinePik subscription, uninstall the app from your Shopify admin. Uninstalling cancels your billing automatically — there's no separate account to close here.</p>
              <Button variant="outline" className="border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white" onClick={() => window.open(shopifyBillingUrl, '_blank', 'noopener,noreferrer')} iconRight={<ExternalLink className="w-4 h-4" data-id="element-13069" data-mp="2p4n0hs0pk6m0" />} data-id="element-13068" data-mp="18m35rwh9oklo">
                Uninstall in Shopify
              </Button>
            </div>
            <p className="text-[14px] text-[#DC2626] flex items-start gap-2" data-id="element-13070" data-mp="32wbddxis6z41">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-13071" data-mp="2y2fgkye97iag" />
              Uninstalling ends your subscription and you'll lose access to your subscription data, member history and settings.
            </p>
          </div>
        </Card>
      </main>
    </div>;
}